import { FC, useId, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectMeta } from '@/shared/model';
import {
    ALLOWED_VAT_RATES,
    VAT_RATE_RESIDENTIAL,
    VAT_RATE_COMMERCIAL,
    VAT_RATE_DEFAULT,
} from '@/shared/lib/vat-constants';

interface ProjectMetaFormProps {
    meta?: ProjectMeta;
    updateMeta?: (updates: Partial<ProjectMeta>) => void;
}

/** Maximum logo file size: 500 KB (in bytes). */
const LOGO_MAX_BYTES = 500 * 1024;

/** Accepted image MIME types for the logo picker.
 * SVG is intentionally excluded: @react-pdf/renderer <Image> does not render SVG,
 * so an SVG upload would be silently dropped from the PDF (CR-02). */
const LOGO_ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp';

export const ProjectMetaForm: FC<ProjectMetaFormProps> = ({
    meta = {},
    updateMeta
}) => {
    const { t } = useTranslation();
    const clientId = useId();
    const addressId = useId();
    const contractorId = useId();
    const contractId = useId();
    const contractorAddressId = useId();
    const contractorNipId = useId();
    const vatRateId = useId();
    const logoId = useId();
    const logoInputRef = useRef<HTMLInputElement>(null);

    const currentVatRate = meta.vatRate ?? VAT_RATE_DEFAULT;

    const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reject non-image MIME types (T-05-07: DoS mitigation)
        if (!file.type.startsWith('image/')) {
            alert(t('editor.sidebar.logo_invalid_type', 'Только изображения (PNG, JPG, WebP)'));
            e.target.value = '';
            return;
        }

        // Reject oversized files (T-05-07: bounded size ~500 KB)
        if (file.size > LOGO_MAX_BYTES) {
            alert(t('editor.sidebar.logo_too_large', 'Логотип слишком большой (макс. 500 КБ)'));
            e.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
            const result = ev.target?.result;
            if (typeof result === 'string') {
                updateMeta?.({ logoBase64: result });
            }
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveLogo = () => {
        updateMeta?.({ logoBase64: undefined });
        if (logoInputRef.current) {
            logoInputRef.current.value = '';
        }
    };

    const inputClass =
        'w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl text-sm font-bold outline-none focus:border-blue-500 transition-all';
    const labelClass =
        'text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1 block cursor-pointer';
    const smallInputClass =
        'w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-lg text-[11px] font-bold outline-none focus:border-blue-500 transition-all';
    const smallLabelClass =
        'text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider ml-1 block cursor-pointer';

    return (
        <div className="space-y-3">
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-2">
                {t('editor.sidebar.project_info', 'Информация о проекте')}
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/30 rounded-2xl p-4 border border-slate-100 dark:border-slate-800 space-y-3">
                {/* Client name */}
                <div className="space-y-1">
                    <label htmlFor={clientId} className={labelClass}>
                        {t('editor.sidebar.client_name', 'Заказчик')}
                    </label>
                    <input
                        id={clientId}
                        value={meta.clientName || ''}
                        onChange={(e) => updateMeta?.({ clientName: e.target.value })}
                        placeholder={t('editor.sidebar.client_placeholder', 'ФИО или Название')}
                        className={inputClass}
                    />
                </div>

                {/* Object address */}
                <div className="space-y-1">
                    <label htmlFor={addressId} className={labelClass}>
                        {t('editor.sidebar.address', 'Адрес объекта')}
                    </label>
                    <input
                        id={addressId}
                        value={meta.clientAddress || ''}
                        onChange={(e) => updateMeta?.({ clientAddress: e.target.value })}
                        placeholder={t('editor.sidebar.address_placeholder', 'Город, улица...')}
                        className={inputClass}
                    />
                </div>

                {/* Contractor name + contract */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <div className="space-y-1">
                        <label htmlFor={contractorId} className={smallLabelClass}>
                            {t('editor.sidebar.contractor', 'Исполнитель')}
                        </label>
                        <input
                            id={contractorId}
                            value={meta.contractorName || ''}
                            onChange={(e) => updateMeta?.({ contractorName: e.target.value })}
                            className={smallInputClass}
                        />
                    </div>
                    <div className="space-y-1">
                        <label htmlFor={contractId} className={smallLabelClass}>
                            {t('editor.sidebar.contract', 'Договор №')}
                        </label>
                        <input
                            id={contractId}
                            value={meta.contractNumber || ''}
                            onChange={(e) => updateMeta?.({ contractNumber: e.target.value })}
                            className={smallInputClass}
                        />
                    </div>
                </div>

                {/* Contractor address */}
                <div className="space-y-1">
                    <label htmlFor={contractorAddressId} className={labelClass}>
                        {t('editor.sidebar.contractor_address', 'Адрес исполнителя')}
                    </label>
                    <input
                        id={contractorAddressId}
                        value={meta.contractorAddress || ''}
                        onChange={(e) => updateMeta?.({ contractorAddress: e.target.value })}
                        placeholder={t('editor.sidebar.contractor_address_placeholder', 'Адрес компании...')}
                        className={inputClass}
                    />
                </div>

                {/* Contractor NIP */}
                <div className="space-y-1">
                    <label htmlFor={contractorNipId} className={labelClass}>
                        {t('editor.sidebar.contractor_nip', 'NIP исполнителя')}
                    </label>
                    <input
                        id={contractorNipId}
                        value={meta.contractorNip || ''}
                        onChange={(e) => updateMeta?.({ contractorNip: e.target.value })}
                        placeholder={t('editor.sidebar.contractor_nip_placeholder', '000-000-00-00')}
                        className={inputClass}
                    />
                </div>

                {/* VAT Rate picker (PDF-02) — options come from ALLOWED_VAT_RATES constants */}
                <div className="space-y-1 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <label htmlFor={vatRateId} className={labelClass}>
                        {t('editor.sidebar.vat_rate', 'Ставка НДС')}
                    </label>
                    <select
                        id={vatRateId}
                        value={currentVatRate}
                        onChange={(e) => updateMeta?.({ vatRate: Number(e.target.value) })}
                        className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl text-sm font-bold outline-none focus:border-blue-500 transition-all cursor-pointer"
                    >
                        {ALLOWED_VAT_RATES.map((rate) => (
                            <option key={rate} value={rate}>
                                {rate === VAT_RATE_RESIDENTIAL
                                    ? t('editor.sidebar.vat_residential', '8% — mieszkaniowe (жилое)')
                                    : rate === VAT_RATE_COMMERCIAL
                                    ? t('editor.sidebar.vat_commercial', '23% — komercyjne (коммерческое)')
                                    : String(Math.round(rate * 100)) + '%'}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Logo upload (T-05-07: validates MIME + size before storing) */}
                <div className="space-y-1">
                    <label htmlFor={logoId} className={labelClass}>
                        {t('editor.sidebar.logo', 'Логотип')}
                    </label>
                    {meta.logoBase64 ? (
                        <div className="flex items-center gap-2">
                            <img
                                src={meta.logoBase64}
                                alt={t('editor.sidebar.logo', 'Логотип')}
                                className="h-10 max-w-[120px] object-contain rounded border border-slate-200 dark:border-slate-700 bg-white p-1"
                            />
                            <button
                                type="button"
                                onClick={handleRemoveLogo}
                                className="text-xs text-red-500 hover:text-red-700 transition-colors"
                            >
                                {t('common.delete', 'Удалить')}
                            </button>
                        </div>
                    ) : (
                        <label
                            htmlFor={logoId}
                            className="flex items-center justify-center w-full h-10 px-3 bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-600 rounded-xl text-xs text-slate-400 cursor-pointer hover:border-blue-400 hover:text-blue-500 transition-all"
                        >
                            {t('editor.sidebar.logo_upload', 'Нажмите для загрузки (макс. 500 КБ)')}
                        </label>
                    )}
                    <input
                        ref={logoInputRef}
                        id={logoId}
                        type="file"
                        accept={LOGO_ACCEPTED_TYPES}
                        onChange={handleLogoChange}
                        className="sr-only"
                    />
                </div>
            </div>
        </div>
    );
};
